import React, { useState } from 'react'
import { Formik } from 'formik'
import * as yup from 'yup'
import {
  ModalInjectedProps,
  Modal,
  Button,
  useModalContext,
  Flex,
} from '@sketch/components'
import {
  ModalStyled,
  OrderedBreadcrumbs,
  FullWidthSeparator,
  StepContainer,
  EditModalHeader,
  TabContentContainer,
  CreatePreviewContainer,
} from './PartialShareModal.styles'
import { SettingsTab } from './SettingsTab'
import { ContentsTab } from './ContentsTab'
import { PreviewFormValues, PreviewFormProvider } from './usePreviewForm'
import {
  useCreatePreviewMutation,
  GetSharePreviewQuery,
} from '@sketch/gql-types'
import { PreviewCreatedSuccessModal } from './PreviewCreatedSuccessModal'

const validationSchema = yup.object({
  previewName: yup
    .string()
    .required('Preview name is required')
    .test(
      'not-only-whitespace',
      'Your preview name cannot contain only spaces',
      value => (value ? value.trim().length > 0 : true)
    ),
  description: yup.string(),
  password: yup.string().when(['passwordProtected', 'hasExistingPassword'], {
    is: (passwordProtected: boolean, hasExistingPassword: boolean) =>
      passwordProtected && !hasExistingPassword,
    then: schema =>
      schema
        .required('Password is required when password protection is enabled')
        .min(6, 'Password must be at least 6 characters')
        .test(
          'not-only-whitespace',
          'Password cannot contain only spaces',
          value => (value ? value.trim().length > 0 : true)
        ),
    otherwise: schema =>
      schema
        .test(
          'password-change',
          'Password must be at least 6 characters',
          value => !value || value.length >= 6
        )
        .test(
          'not-only-whitespace',
          'Password cannot be only whitespace',
          value => !value || value.trim().length > 0
        ),
  }),
  expiryDate: yup.string().when('expiryDateEnabled', {
    is: true,
    then: schema =>
      schema.required('Date is required when preview expiration is enabled'),
  }),
  selectedUUIDs: yup
    .array(
      yup.object({
        uuid: yup.string().required(),
        type: yup.string().oneOf(['PAGE', 'PROTOTYPE']).required(),
      })
    )
    .min(1, 'At least one item must be selected'),
})

// Use the preview type from the GraphQL query
export type PreviewType = NonNullable<GetSharePreviewQuery['share']>['preview']

const getInitialValues = (): PreviewFormValues => ({
  previewName: '',
  description: '',
  inspectEnabled: false,
  commentsEnabled: false,
  passwordProtected: false,
  password: '',
  hasExistingPassword: false,
  expiryDate: '',
  expiryDateEnabled: false,
  selectedUUIDs: [],
})

interface PartialShareModalCreateProps extends ModalInjectedProps {
  shareName: string
  shareIdentifier: string
  shareThumbnail?: string
  versionShortId: string
}

interface BreadcrumbsProps {
  step: number
}

const Breadcrumbs = ({ step }: BreadcrumbsProps) => {
  const breadcrumbs = [
    { content: 'Preview Set Up' },
    { content: 'Preview Contents' },
  ]

  return (
    <OrderedBreadcrumbs
      currentCrumb={step}
      crumbs={breadcrumbs}
      showAllInMobile
    />
  )
}

export const PartialShareModalCreate = ({
  shareName,
  shareIdentifier,
  shareThumbnail,
  versionShortId,
  hideModal,
}: PartialShareModalCreateProps) => {
  const { showModal } = useModalContext()

  const [step, setStep] = useState(0)

  const [createPreview] = useCreatePreviewMutation({
    onCompleted: ({ createPreview }) => {
      showModal(PreviewCreatedSuccessModal, {
        previewId: createPreview.preview.identifier,
      })
    },
    onError: 'show-toast',
  })

  const handleSubmit = (values: PreviewFormValues) => {
    const expiryDate =
      values.expiryDateEnabled && !!values.expiryDate
        ? new Date(values.expiryDate).toISOString()
        : undefined

    return createPreview({
      variables: {
        shareIdentifier,
        name: values.previewName,
        description: values.description,
        inspectEnabled: values.inspectEnabled,
        commentsEnabled: values.commentsEnabled,
        expiryDate,
        password: values.password.trim(),
        // although we're dealing with UUIDs, the mutation expects them to be under identifier
        content: values.selectedUUIDs.map(selected => ({
          identifier: selected.uuid,
          type: selected.type,
        })),
      },
    })
  }

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <SettingsTab
            shareName={shareName}
            shareThumbnail={shareThumbnail}
            shareIdentifier={shareIdentifier}
          />
        )
      case 1: {
        return (
          <ContentsTab
            shareIdentifier={shareIdentifier}
            versionShortId={versionShortId}
          />
        )
      }
    }
  }

  const prevStep = () => {
    setStep(prev => prev - 1)
  }

  const nextStep = () => {
    setStep(prev => prev + 1)
  }

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      validateOnMount={true}
      onSubmit={handleSubmit}
    >
      {formikBag => {
        const settingsFields = {
          previewName: true,
          description: true,
          passwordProtected: true,
          password: true,
          hasExistingPassword: true,
          expiryDate: true,
          expiryDateEnabled: true,
          inspectEnabled: true,
          commentsEnabled: true,
        }

        // Check if there are any errors in the settings fields
        const hasSettingsErrors = Object.keys(formikBag.errors).some(
          key => key in settingsFields
        )

        const handleSubmitButtonAction = async () => {
          switch (step) {
            case 0: {
              // Validate only SettingsTab fields before advancing
              const errors = await formikBag.validateForm()
              formikBag.setTouched(settingsFields)

              // Check if there are any errors in the settings fields
              const hasSettingsErrors = Object.keys(errors).some(
                key => key in settingsFields
              )

              if (!hasSettingsErrors) {
                nextStep()
              }
              break
            }
            case 1: {
              // Submit the form
              formikBag.submitForm()
              break
            }
          }
        }

        return (
          <ModalStyled
            onCancel={hideModal}
            autoFocusOnOpen={false}
            cancelOnClickOutside={false}
          >
            <PreviewFormProvider>
              <CreatePreviewContainer>
                <EditModalHeader>
                  <Flex alignItems="center" height={48}>
                    <Breadcrumbs step={step} />
                  </Flex>
                  <FullWidthSeparator />
                </EditModalHeader>
                <StepContainer>
                  <TabContentContainer>{renderStep()}</TabContentContainer>
                </StepContainer>
                <PreviewFormFooter
                  hideModal={hideModal}
                  previousStep={prevStep}
                  onSubmitButtonAction={handleSubmitButtonAction}
                  step={step}
                  isSubmitting={formikBag.isSubmitting}
                  isValid={formikBag.isValid}
                  hasSettingsErrors={hasSettingsErrors}
                />
              </CreatePreviewContainer>
            </PreviewFormProvider>
          </ModalStyled>
        )
      }}
    </Formik>
  )
}

function PreviewFormFooter({
  hideModal,
  previousStep,
  onSubmitButtonAction,
  step,
  isSubmitting,
  isValid,
  hasSettingsErrors,
}: {
  hideModal: () => void
  previousStep: () => void
  onSubmitButtonAction: () => void
  step: number
  isSubmitting: boolean
  isValid: boolean
  hasSettingsErrors: boolean
}) {
  const getSubmitButtonText = () => {
    if (step === 0) {
      return 'Next'
    }
    if (isSubmitting) {
      return 'Creating...'
    }
    return 'Create Preview'
  }

  return (
    <Modal.Footer>
      <Button
        variant="secondary"
        disabled={isSubmitting}
        size="40"
        onClick={hideModal}
      >
        Cancel
      </Button>

      <Button
        variant="secondary"
        disabled={isSubmitting || step === 0}
        size="40"
        onClick={previousStep}
      >
        Back
      </Button>

      <Button
        variant="primary"
        disabled={
          isSubmitting ||
          (step === 0 && hasSettingsErrors) ||
          (step === 1 && !isValid)
        }
        size="40"
        onClick={onSubmitButtonAction}
      >
        {getSubmitButtonText()}
      </Button>
    </Modal.Footer>
  )
}
